import type { GenericFormType } from '@neo/blocks/forms/types'
import { DraggableList, DragListIdKeys } from '@neo/blocks/sortable-list'
import { Button } from '@neo/index'
// import { Flag } from '@neo/primitives/icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { Tooltip } from '@neo/primitives/tooltip'
import { CirclePlus, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import type { RBusinessHours } from 'v2/types/api/settings'
import { useBusinessHoursQuery } from '../../queries'
import type { BusinessHourNodeData } from '../../types'

export const AddBusinessHours = ({
  form,
}: {
  form: GenericFormType<BusinessHourNodeData['config']>
}) => {
  const [selectedHours, setSelectedHours] = useState<RBusinessHours[]>(
    form.state.values.business_hrs,
  )
  const [isOpen, setIsOpen] = useState(false)
  const { ref, inView } = useInView()

  const handleSelectHour = (hour: RBusinessHours) => {
    let updatedHours: RBusinessHours[]
    if (selectedHours.some((h) => h.id === hour.id)) {
      updatedHours = selectedHours.filter((h) => h.id !== hour.id)
    } else {
      updatedHours = [...selectedHours, hour]
    }
    setSelectedHours(updatedHours)
    form.setFieldValue('business_hrs', updatedHours)
    form.validateField('business_hrs', 'change')
  }

  const { data: businessHours, fetchNextPage, hasNextPage } = useBusinessHoursQuery()

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView])

  const businessHoursData =
    businessHours?.pages.flat()?.filter((h) => !selectedHours.some((sh) => sh.id === h.id)) || []

  return (
    <div className="w-full max-w-3xl mx-auto p-4 pl-0">
      <div className="relative">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" type="button" className="gap-2 p-0 hover:bg-transparent">
              <CirclePlus className="h-4 w-4" />
              Add Business Hour
            </Button>
          </DropdownMenuTrigger>
          <div>
            <DropdownMenuContent
              className="w-[calc(100vw-2rem)] max-w-[367px] max-h-[218px] overflow-y-auto mr-4 p-1"
              align="start"
              alignOffset={0}
              sideOffset={4}
            >
              <div>
                {businessHoursData.map((hour) => (
                  <DropdownMenuItem
                    key={hour.id}
                    className="flex items-center justify-between cursor-pointer"
                    onSelect={(event) => event.preventDefault()}
                  >
                    <div className="flex flex-col w-4/5">
                      {/* <Flag iso={hour.timezone.short_form} circle size={16} /> */}
                      {/* <div> */}
                      <div className="font-inter text-foreground text-s-400 text-ellipsis overflow-hidden">
                        {hour.name}
                      </div>
                      <div className="text-secondary text-xs-400 mt-0.5">{hour.timezone.name}</div>
                      {/* </div> */}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="p-0 h-fit w-fit"
                      type="button"
                      onClick={() => {
                        handleSelectHour(hour)
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      <span className="sr-only">Add {hour.name}</span>
                    </Button>
                  </DropdownMenuItem>
                ))}
                {hasNextPage && (
                  <div ref={ref} className="py-2 px-4 text-center text-s-400 text-muted-foreground">
                    Loading more...
                  </div>
                )}
                {businessHoursData.length === 0 && (
                  <div className="py-4 px-4 text-center text-s-400 text-muted-foreground">
                    No business hours found
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </div>
        </DropdownMenu>
      </div>
      {selectedHours.length > 0 && (
        <DraggableList
          idKeyName={DragListIdKeys.id}
          list={selectedHours}
          className="flex mt-2 gap-2 py-3 px-4 hover:bg-bg2 rounded-lg border border-st2"
          onDragEnd={(reOrderedList) => {
            setSelectedHours(reOrderedList)
            form.setFieldValue('business_hrs', reOrderedList)
          }}
        >
          {(subItem) => (
            <li className="flex">
              <div className="flex flex-col gap-3 flex-1">
                {/* <div className="w-8 h-8 rounded-full overflow-hidden">
                  <Flag iso={subItem.timezone.short_form} circle size={16} />
                </div> */}
                <div>
                  <div className="font-inter text-foreground text-s-500 flex items-center overflow-hidden justify-between hover:text-primary group w-[calc(100%-0.5rem)]">
                    <div className="text-ellipsis overflow-hidden p-0">{subItem.name}</div>
                    <Tooltip content="Remove business hour">
                      <Button
                        variant="ghost"
                        onClick={() => handleSelectHour(subItem)}
                        type="button"
                        aria-label="Remove"
                        className="p-0 h-auto w-auto invisible group-hover:visible text-status-danger hover:text-status-danger"
                      >
                        <Trash2 className="h-4 w-4" size={16} />
                      </Button>
                    </Tooltip>
                  </div>
                  <div className="text-secondary text-s-400">{subItem.timezone.name}</div>
                </div>
              </div>
            </li>
          )}
        </DraggableList>
      )}
    </div>
  )
}
